import { useState } from 'react';
import type {
  AdminConversation,
  ProfileMemoryState,
  ProfileRefineryMode,
} from '../../api/types.js';
import { formatRelative } from '../../lib/utils.js';

interface ProfileMemoryPanelProps {
  conversation: AdminConversation;
  onRunProfileRefinery: (friendId: number, mode: ProfileRefineryMode) => Promise<void>;
  onResolveWarnings: (friendId: number) => Promise<void>;
}

export function ProfileMemoryPanel({ conversation, onRunProfileRefinery, onResolveWarnings }: ProfileMemoryPanelProps) {
  const [runningMode, setRunningMode] = useState<ProfileRefineryMode | null>(null);
  const [resolvingWarnings, setResolvingWarnings] = useState(false);
  const memory = conversation.salesState?.profileMemory ?? null;
  const warnings = Array.isArray(memory?.warnings) ? memory.warnings : [];
  const status = memory?.status || 'none';
  const lastRun = memory?.lastRun ?? null;

  async function run(mode: ProfileRefineryMode) {
    setRunningMode(mode);
    try {
      await onRunProfileRefinery(conversation.friendId, mode);
    } finally {
      setRunningMode(null);
    }
  }

  async function resolveWarnings() {
    setResolvingWarnings(true);
    try {
      await onResolveWarnings(conversation.friendId);
    } finally {
      setResolvingWarnings(false);
    }
  }

  return (
    <div className="profile-card">
      <section className="profile-section">
        <div className="profile-section-heading">
          <h3>档案卡状态</h3>
          <span className={`profile-memory-status ${statusClass(status)}`}>{statusText(status)}</span>
        </div>
        <div className="profile-memory-grid">
          <ProfileMemoryFact label="版本" value={memory?.version || '未生成'} />
          <ProfileMemoryFact label="最近整理" value={formatNullableDate(memory?.lastRefinedAt)} />
          <ProfileMemoryFact label="最近验收" value={formatNullableDate(memory?.acceptedAt)} />
          <ProfileMemoryFact label="水位消息" value={memory?.lastRefinedMessageId ? String(memory.lastRefinedMessageId) : '-'} />
        </div>
        {memory?.lastGateReason ? (
          <div className="profile-memory-note">接管门：{memory.lastGateReason}</div>
        ) : null}
      </section>

      <section className="profile-section">
        <h3>整理操作</h3>
        <div className="profile-memory-actions">
          <button
            type="button"
            onClick={() => void run('full')}
            disabled={Boolean(runningMode)}
          >
            {runningMode === 'full' ? '整理中...' : '全量整理'}
          </button>
          <button
            type="button"
            onClick={() => void run('accept')}
            disabled={Boolean(runningMode)}
          >
            {runningMode === 'accept' ? '验收中...' : '验收通过'}
          </button>
        </div>
      </section>

      <section className="profile-section">
        <div className="profile-section-heading">
          <h3>Warnings</h3>
          <button
            className="profile-memory-secondary"
            type="button"
            onClick={() => void resolveWarnings()}
            disabled={warnings.length === 0 || resolvingWarnings}
          >
            {resolvingWarnings ? '处理中...' : '标记已处理'}
          </button>
        </div>
        {warnings.length > 0 ? (
          <div className="profile-memory-warnings">
            {warnings.map((warning, index) => (
              <div key={`${index}-${warning}`}>{warning}</div>
            ))}
          </div>
        ) : (
          <div className="profile-muted">暂无 warning</div>
        )}
        {memory?.warningsHandledAt ? (
          <div className="profile-memory-note">
            最近处理：{formatNullableDate(memory.warningsHandledAt)}
            {memory.warningsHandledBy ? ` · ${memory.warningsHandledBy}` : ''}
          </div>
        ) : null}
      </section>

      <section className="profile-section">
        <h3>最近运行</h3>
        {lastRun ? (
          <div className="profile-memory-run">
            <ProfileMemoryFact label="模式" value={lastRun.mode || '-'} />
            <ProfileMemoryFact label="时间" value={formatNullableDate(lastRun.ranAt)} />
            <ProfileMemoryFact label="消息数" value={lastRun.messageCount !== undefined ? String(lastRun.messageCount) : '-'} />
            <ProfileMemoryFact label="Session" value={lastRun.sessionId || '-'} />
            {lastRun.error ? <div className="profile-memory-note danger">{lastRun.error}</div> : null}
          </div>
        ) : (
          <div className="profile-muted">暂无整理运行记录</div>
        )}
      </section>
    </div>
  );
}

function ProfileMemoryFact({ label, value }: { label: string; value: string }) {
  return (
    <div className="profile-memory-fact">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function statusText(status: ProfileMemoryState['status']): string {
  if (status === 'accepted') return '已验收';
  if (status === 'pending') return '待验收';
  if (status === 'failed') return '失败';
  if (status === 'none') return '未生成';
  return status || '未生成';
}

function statusClass(status: string): string {
  if (status === 'accepted') return 'accepted';
  if (status === 'failed') return 'failed';
  if (status === 'pending') return 'pending';
  return 'empty';
}

function formatNullableDate(value?: string | null): string {
  return value ? formatRelative(value) : '-';
}
